/**
 * Client Portal — OTP (one-time code) login. The primary way clients sign in.
 *
 * Two steps, both brand-neutral (the client isn't identified until they're in):
 *   1. Email → "Send code". Always advances to step 2 on a 200 (the backend
 *      never enumerates accounts — it only actually emails a code when the
 *      email already has portal access).
 *   2. 6-digit code → "Verify". On success we commit the returned access token
 *      + identity to Redux (`auth.clientPortal.*`) exactly like MagicLink.tsx
 *      and land on the dashboard. A wrong/expired code (401) shows an inline
 *      error and lets the user retry or resend.
 *
 * The magic-link flow (`/client-portal/auth`) still works; this is the code-
 * based alternative. Per CLAUDE.md the access token lives in Redux ONLY.
 */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  requestOtp,
  verifyOtp,
} from '../../lib/api/client-portal/client-portal-api';
import { setClientPortalSession } from '../../store/reducers/authSlice';
import { PORTAL_NEUTRAL } from './brand';

type Step = 'email' | 'code';

const ClientPortalOtpLogin: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Optional brand hint — an invite deep-link may carry `?shop=<slug>` so the
  // backend can scope the code. Absence is fine (contract makes it optional).
  const shopSlug = searchParams.get('shop') || undefined;

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const emailValid = /\S+@\S+\.\S+/.test(email.trim());
  const codeValid = /^\d{6}$/.test(code.trim());

  const sendCode = async () => {
    if (!emailValid || sending) return;
    setSending(true);
    setError(null);
    try {
      await requestOtp(email.trim(), shopSlug);
      // Always advance on a 200 — the backend never reveals whether the email
      // has portal access, so we show the same neutral confirmation regardless.
      setNotice(
        "If that email has portal access, we've sent a 6-digit code. It expires shortly."
      );
      setStep('code');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ClientPortalOtpLogin] request-otp failed', err);
      setError('We could not send a code right now. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    if (!codeValid || verifying) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await verifyOtp(email.trim(), code.trim());
      dispatch(
        setClientPortalSession({
          accessToken: res.access_token,
          expiresAt: res.expires_at,
          identity: res.client,
        })
      );
      navigate('/client-portal/dashboard', { replace: true });
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401) {
        setError('Invalid or expired code. Request a new one and try again.');
      } else {
        // eslint-disable-next-line no-console
        console.error('[ClientPortalOtpLogin] verify-otp failed', err);
        setError('We could not verify that code. Please try again.');
      }
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setCode('');
    setError(null);
    await sendCode();
  };

  const editEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setNotice(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f6f8',
        px: 2,
      }}
    >
      <Container maxWidth='xs' disableGutters>
        <Paper
          elevation={0}
          variant='outlined'
          sx={{ p: 4, borderRadius: 3 }}
        >
          <Stack spacing={2.5}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant='h6' component='h1' sx={{ fontWeight: 700 }}>
                Sign in to your portal
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}
              >
                {step === 'email'
                  ? "Enter your email and we'll send you a one-time code."
                  : `Enter the 6-digit code we sent to ${email.trim()}.`}
              </Typography>
            </Box>

            {error && (
              <Alert severity='error' sx={{ textAlign: 'left' }}>
                {error}
              </Alert>
            )}
            {step === 'code' && notice && !error && (
              <Alert severity='info' sx={{ textAlign: 'left' }}>
                {notice}
              </Alert>
            )}

            {step === 'email' ? (
              <>
                <TextField
                  label='Email'
                  type='email'
                  fullWidth
                  autoFocus
                  autoComplete='email'
                  value={email}
                  disabled={sending}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') sendCode();
                  }}
                />
                <Button
                  variant='contained'
                  fullWidth
                  disabled={!emailValid || sending}
                  onClick={sendCode}
                  sx={{
                    backgroundColor: PORTAL_NEUTRAL,
                    '&:hover': { backgroundColor: '#000' },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.1,
                  }}
                >
                  {sending ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    'Send code'
                  )}
                </Button>
              </>
            ) : (
              <>
                <TextField
                  label='6-digit code'
                  fullWidth
                  autoFocus
                  autoComplete='one-time-code'
                  inputMode='numeric'
                  value={code}
                  disabled={verifying}
                  inputProps={{
                    maxLength: 6,
                    style: { letterSpacing: '0.4em', textAlign: 'center' },
                  }}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') verify();
                  }}
                />
                <Button
                  variant='contained'
                  fullWidth
                  disabled={!codeValid || verifying}
                  onClick={verify}
                  sx={{
                    backgroundColor: PORTAL_NEUTRAL,
                    '&:hover': { backgroundColor: '#000' },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.1,
                  }}
                >
                  {verifying ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    'Verify'
                  )}
                </Button>
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <MuiLink
                    component='button'
                    type='button'
                    underline='hover'
                    color='text.secondary'
                    onClick={editEmail}
                    sx={{ fontSize: 13, cursor: 'pointer' }}
                  >
                    Use a different email
                  </MuiLink>
                  <MuiLink
                    component='button'
                    type='button'
                    underline='hover'
                    color='text.secondary'
                    onClick={resend}
                    sx={{ fontSize: 13, cursor: 'pointer' }}
                  >
                    {sending ? 'Sending…' : 'Resend code'}
                  </MuiLink>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ClientPortalOtpLogin;
